import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  addDoc,
  arrayUnion,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase';

const MAX_GROUP_SIZE = 5;

export async function findOrCreateGroup(userId, userData) {
  try {
    if (!userId) throw new Error('No userId provided');
    if (!userData?.goalCategory) throw new Error('No goalCategory on userData');
    if (!userData?.ageGroup) throw new Error('No ageGroup on userData');

    const groupsRef = collection(db, 'groups');
    const q = query(
      groupsRef,
      where('status', '==', 'open'),
      where('goalCategory', '==', userData.goalCategory),
      where('ageGroup', '==', userData.ageGroup) // ← age gate
    );

    const snapshot = await getDocs(q);
    let targetGroup = null;

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      const memberIds = data.memberIds || [];
      if (memberIds.includes(userId)) return;
      const blockedUsers = userData.blockedUsers || [];
      const hasBlockedMember = memberIds.some(id => blockedUsers.includes(id));
      if (hasBlockedMember) return;
      if (memberIds.length < MAX_GROUP_SIZE && !targetGroup) {
        targetGroup = { id: docSnap.id, ...data };
      }
    });

    if (targetGroup) {
      const newMember = {
        uid: userId,
        name: userData.name || 'Anonymous',
        avatarId: userData.avatarId || null,
        joinedAt: new Date().toISOString()
      };
      const newMemberCount = (targetGroup.memberIds || []).length + 1;

      await updateDoc(doc(db, 'groups', targetGroup.id), {
        members: arrayUnion(newMember),
        memberIds: arrayUnion(userId),
        status: newMemberCount >= MAX_GROUP_SIZE ? 'full' : 'open',
        updatedAt: serverTimestamp()
      });

      await updateDoc(doc(db, 'users', userId), { groupId: targetGroup.id });
      return { groupId: targetGroup.id, created: false };
    }

    // Create new age-scoped group
    const newGroupRef = await addDoc(collection(db, 'groups'), {
      name: `${userData.goalCategory} Group`,
      goalCategory: userData.goalCategory,
      ageGroup: userData.ageGroup, // ← stored on group
      createdBy: userId,
      members: [{
        uid: userId,
        name: userData.name || 'Anonymous',
        avatarId: userData.avatarId || null,
        joinedAt: new Date().toISOString()
      }],
      memberIds: [userId],
      status: 'open',
      maxMembers: MAX_GROUP_SIZE,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    await updateDoc(doc(db, 'users', userId), { groupId: newGroupRef.id });
    return { groupId: newGroupRef.id, created: true };

  } catch (error) {
    console.error('findOrCreateGroup error:', error.code, error.message);
    throw error;
  }
}

export async function leaveGroup(userId, groupId) {
  try {
    const groupSnap = await getDoc(doc(db, 'groups', groupId));
    if (!groupSnap.exists()) return;

    const groupData = groupSnap.data();
    const updatedMembers = (groupData.members || []).filter(m => m.uid !== userId);
    const updatedMemberIds = updatedMembers.map(m => m.uid);

    if (updatedMembers.length === 0) {
      await updateDoc(doc(db, 'groups', groupId), {
        status: 'disbanded', members: [], memberIds: [], updatedAt: serverTimestamp()
      });
    } else {
      await updateDoc(doc(db, 'groups', groupId), {
        members: updatedMembers, memberIds: updatedMemberIds,
        status: updatedMembers.length < MAX_GROUP_SIZE ? 'open' : 'full',
        updatedAt: serverTimestamp()
      });
    }

    await updateDoc(doc(db, 'users', userId), { groupId: null });
    return true;
  } catch (error) {
    console.error('leaveGroup error:', error.code, error.message);
    throw error;
  }
}

export async function getGroup(groupId) {
  try {
    const groupSnap = await getDoc(doc(db, 'groups', groupId));
    if (!groupSnap.exists()) return null;
    return { id: groupSnap.id, ...groupSnap.data() };
  } catch (error) {
    console.error('getGroup error:', error.code, error.message);
    return null;
  }
}